import React, { useMemo, useRef } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { keys as LOCALSTORAGE_KEYS } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';

import { learnMoreURL } from '../constants.ts';

const i18n = defineMessages({
	pageHasBeenConvertedTitle: {
		id: 'editor-features.page-has-been-converted-title',
		description:
			'Page Opt In Migration Flag title to show that page has been recently converted to Fabric',
		defaultMessage: 'Page has been converted',
	},
	pageHasBeenConvertedDescription: {
		id: 'editor-features.page-has-been-converted-description',
		description:
			'Page Opt In Migration Flag description to show that page has been recently converted to Fabric',
		defaultMessage: 'This page has been converted to the new editing experience',
	},
	pageHasBeenConvertedLearnMore: {
		id: 'editor-features.page-has-been-converted-learn-more-link',
		description:
			'Page Opt In Migration Flag call to action to direct user to url where they learn more about pages converted to new fabric editor',
		defaultMessage: 'Learn more',
	},
});

type MigrationSuccessFlagComponentProps = {
	contentId: string;
	flags: FlagsStateContainer;
};

const onClickLearnMore = () => {
	window.open(learnMoreURL, '_blank');
};

const MigrationSuccessFlagComponent = ({
	contentId,
	flags,
}: MigrationSuccessFlagComponentProps) => {
	// We only want to show the flag once (we don't want to re-render and show the flag again)
	const didShowFlag = useRef<boolean>(false);
	// This key is set previously when a user manually converts a page (i.e. clicks "Convert page")
	const didManualConversionRecently =
		localStorage.getItem(LOCALSTORAGE_KEYS.PAGE_JUST_CONVERTED) === contentId;
	// We want to clear the previously set key since we only want to show the flag when the user first lands in the Fabric editor
	const onClose = () => localStorage.removeItem(LOCALSTORAGE_KEYS.PAGE_JUST_CONVERTED);

	const shouldShowFlag = useMemo(
		() => didManualConversionRecently && !!flags,
		[didManualConversionRecently, flags],
	);

	if (shouldShowFlag && !didShowFlag.current) {
		didShowFlag.current = true;
		void flags.showInfoFlag({
			title: <FormattedMessage {...i18n.pageHasBeenConvertedTitle} />,
			description: <FormattedMessage {...i18n.pageHasBeenConvertedDescription} />,
			actions: [
				{
					content: <FormattedMessage {...i18n.pageHasBeenConvertedLearnMore} />,
					onClick: onClickLearnMore,
				},
			],
			// Auto-dismiss the flag after 8 seconds (AtlasKit AutoDismissFlag default dismiss delay)
			isAutoDismiss: true,
			onClose,
		});
	}

	return null;
};

export const MigrationSuccessFlag = withFlags(MigrationSuccessFlagComponent);
